import React, { useState, useEffect } from 'react';
import { 
  Brain, MessageCircle, Settings, TrendingUp, Sparkles, Clock, Star, Bot,
  BookOpen, GraduationCap, Target, Calendar, Award, FileText, Lightbulb,
  Users, DollarSign, BarChart3, Shield, Activity, PieChart, LineChart,
  Zap, Heart, CheckCircle, ArrowRight, Play, ChevronRight, Mic
} from 'lucide-react';
import { SmartRecommendations } from '../../components/AI/SmartRecommendations';
import { RoleBasedAIChatbot, RoleBasedChatbotTrigger } from '../../components/AI/RoleBasedAIChatbot';
import { PreferencesSettings } from '../../components/AI/PreferencesSettings';
import { useAuthStore } from '../../stores/authStore';

interface StudentStats {
  studyHours: number;
  completedCourses: number;
  examAverage: number;
  aiInteractions: number;
}

// 学生端 AI 功能
const StudentAIFeatures: React.FC<{ onOpenChat: () => void }> = ({ onOpenChat }) => {
  const [activeFeature, setActiveFeature] = useState<'plan' | 'course' | 'exam' | 'qa'>('plan');
  const [stats, setStats] = useState<StudentStats>({ studyHours: 0, completedCourses: 0, examAverage: 0, aiInteractions: 0 });
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchStats();
  }, []);

  const fetchStats = async () => {
    try {
      const token = localStorage.getItem('token');
      if (!token) return;

      const [statsRes, examRes] = await Promise.allSettled([
        fetch('/api/students/stats', {
          headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' }
        }),
        fetch('/api/students/exams', {
          headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' }
        })
      ]);

      let studyHours = 0;
      let completedCourses = 0;
      let examAverage = 0;

      if (statsRes.status === 'fulfilled' && statsRes.value.ok) {
        const data = await statsRes.value.json();
        studyHours = Math.round(data.totalHours || 0);
        completedCourses = data.completedBookings || 0;
      }

      if (examRes.status === 'fulfilled' && examRes.value.ok) {
        const data = await examRes.value.json();
        examAverage = data.summary?.averageScore || 0;
      }

      // 尝试获取 AI 交互次数
      let aiInteractions = 0;
      try {
        const aiRes = await fetch('/api/ai/interactions/count', {
          headers: { Authorization: `Bearer ${token}` }
        });
        if (aiRes.ok) {
          const aiData = await aiRes.json();
          aiInteractions = aiData.count || aiData.total || 0;
        }
      } catch {
        // 如果 API 不可用，从 localStorage 获取历史记录
        const storedCount = localStorage.getItem('ai_interaction_count');
        aiInteractions = storedCount ? parseInt(storedCount, 10) : 0;
      }

      setStats({
        studyHours,
        completedCourses,
        examAverage: Math.round(examAverage),
        aiInteractions
      });
    } catch (error) {
      console.error('获取统计数据失败:', error);
    } finally {
      setLoading(false);
    }
  };

  const features = [
    {
      id: 'plan' as const,
      title: '智能学习计划',
      description: 'AI 根据你的目标和时间，生成个性化学习路径',
      icon: Calendar,
      color: 'from-blue-500 to-indigo-600',
      actions: ['生成本周计划', '调整学习目标', '查看进度报告']
    },
    {
      id: 'course' as const,
      title: '课程智能推荐',
      description: '基于学习数据和兴趣，推荐最适合的课程',
      icon: BookOpen,
      color: 'from-purple-500 to-pink-600',
      actions: ['发现新课程', '相似课程推荐', '名师推荐']
    },
    {
      id: 'exam' as const,
      title: '智能考试助手',
      description: 'AI 出题、模拟测试、成绩分析一站式服务',
      icon: FileText,
      color: 'from-emerald-500 to-teal-600',
      actions: ['开始模拟测试', '查看错题本', '知识点分析']
    },
    {
      id: 'qa' as const,
      title: '问题答疑中心',
      description: '24小时 AI 助教，随时解答学习疑问',
      icon: Lightbulb,
      color: 'from-amber-500 to-orange-600',
      actions: ['提出问题', '查看历史问答', '知识点讲解']
    }
  ];

  const currentFeature = features.find(f => f.id === activeFeature)!;

  return (
    <div className="space-y-8">
      {/* 功能卡片网格 */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
        {features.map((feature) => (
          <button
            key={feature.id}
            onClick={() => setActiveFeature(feature.id)}
            className={`p-5 rounded-2xl border-2 text-left transition-all ${
              activeFeature === feature.id
                ? 'border-indigo-300 bg-indigo-50 shadow-lg shadow-indigo-100/50'
                : 'border-slate-100 bg-white hover:border-slate-200 hover:shadow-md'
            }`}
          >
            <div className={`w-12 h-12 rounded-xl bg-gradient-to-br ${feature.color} flex items-center justify-center text-white shadow-lg mb-4`}>
              <feature.icon className="w-6 h-6" />
            </div>
            <h3 className="font-bold text-slate-900 mb-1">{feature.title}</h3>
            <p className="text-xs text-slate-500 line-clamp-2">{feature.description}</p>
          </button>
        ))}
      </div>

      {/* 当前功能详情 */}
      <div className="bg-white rounded-3xl border border-slate-100 p-8 shadow-sm">
        <div className="flex items-start justify-between mb-6">
          <div className="flex items-center gap-4">
            <div className={`w-14 h-14 rounded-2xl bg-gradient-to-br ${currentFeature.color} flex items-center justify-center text-white shadow-xl`}>
              <currentFeature.icon className="w-7 h-7" />
            </div>
            <div>
              <h2 className="text-2xl font-bold text-slate-900">{currentFeature.title}</h2>
              <p className="text-slate-500">{currentFeature.description}</p>
            </div>
          </div>
          <button
            onClick={onOpenChat}
            className="flex items-center gap-2 px-5 py-2.5 bg-gradient-to-r from-indigo-500 to-purple-600 text-white rounded-xl font-medium shadow-lg shadow-indigo-200/50 hover:shadow-xl transition-all"
          >
            <MessageCircle className="w-4 h-4" />
            开始对话
          </button>
        </div>

        <div className="grid md:grid-cols-3 gap-4">
          {currentFeature.actions.map((action, index) => (
            <button
              key={action}
              onClick={onOpenChat}
              className="group p-5 rounded-2xl bg-slate-50 hover:bg-indigo-50 border border-slate-100 hover:border-indigo-200 transition-all text-left"
            >
              <div className="flex items-center justify-between">
                <span className="font-medium text-slate-800 group-hover:text-indigo-700">{action}</span>
                <ChevronRight className="w-5 h-5 text-slate-400 group-hover:text-indigo-500 group-hover:translate-x-1 transition-all" />
              </div>
            </button>
          ))}
        </div>
      </div>

      {/* 学习数据概览 */}
      <div className="grid md:grid-cols-4 gap-4">
        {loading ? (
          // 加载骨架屏
          Array.from({ length: 4 }).map((_, i) => (
            <div key={i} className="bg-white rounded-2xl border border-slate-100 p-5 animate-pulse">
              <div className="flex items-center justify-between mb-3">
                <div className="w-10 h-10 rounded-xl bg-slate-200" />
                <div className="w-12 h-5 bg-slate-200 rounded-full" />
              </div>
              <div className="w-20 h-8 bg-slate-200 rounded mb-1" />
              <div className="w-16 h-4 bg-slate-200 rounded" />
            </div>
          ))
        ) : (
          [
            { label: '累计学习时长', value: `${stats.studyHours} 小时`, change: stats.studyHours > 0 ? '已记录' : '暂无', icon: Clock, color: 'blue' },
            { label: '完成课程数', value: `${stats.completedCourses} 节`, change: stats.completedCourses > 0 ? '持续学习' : '开始学习', icon: CheckCircle, color: 'emerald' },
            { label: '考试平均分', value: stats.examAverage > 0 ? `${stats.examAverage} 分` : '暂无', change: stats.examAverage >= 80 ? '优秀' : stats.examAverage > 0 ? '加油' : '待测试', icon: Award, color: 'purple' },
            { label: 'AI 互动次数', value: `${stats.aiInteractions} 次`, change: '活跃', icon: MessageCircle, color: 'amber' }
          ].map((stat) => (
            <div key={stat.label} className="bg-white rounded-2xl border border-slate-100 p-5 hover:shadow-md transition-shadow">
              <div className="flex items-center justify-between mb-3">
                <div className={`w-10 h-10 rounded-xl bg-${stat.color}-100 flex items-center justify-center`}>
                  <stat.icon className={`w-5 h-5 text-${stat.color}-600`} />
                </div>
                <span className="text-xs text-emerald-600 font-medium bg-emerald-50 px-2 py-1 rounded-full">{stat.change}</span>
              </div>
              <div className="text-2xl font-bold text-slate-900">{stat.value}</div>
              <div className="text-xs text-slate-500">{stat.label}</div>
            </div>
          ))
        )}
      </div>
    </div>
  );
};

// 教师端 AI 功能
const TeacherAIFeatures: React.FC<{ onOpenChat: () => void }> = ({ onOpenChat }) => {
  return (
    <div className="space-y-8">
      {/* 核心指标 */}
      <div className="grid md:grid-cols-4 gap-4">
        {[
          { label: '本月收入', value: '¥12,580', change: '+18.5%', icon: DollarSign, color: 'emerald' },
          { label: '活跃学生', value: '42 人', change: '+8', icon: Users, color: 'blue' },
          { label: '课程评分', value: '4.9', change: '+0.2', icon: Star, color: 'amber' },
          { label: '预约率', value: '85%', change: '+12%', icon: TrendingUp, color: 'purple' }
        ].map((stat) => (
          <div key={stat.label} className="bg-white rounded-2xl border border-slate-100 p-5 hover:shadow-lg transition-shadow">
            <div className="flex items-center justify-between mb-3">
              <div className={`w-10 h-10 rounded-xl bg-${stat.color}-100 flex items-center justify-center`}>
                <stat.icon className={`w-5 h-5 text-${stat.color}-600`} />
              </div>
              <span className="text-xs text-emerald-600 font-medium bg-emerald-50 px-2 py-1 rounded-full">{stat.change}</span>
            </div>
            <div className="text-2xl font-bold text-slate-900">{stat.value}</div>
            <div className="text-xs text-slate-500">{stat.label}</div>
          </div>
        ))}
      </div>

      {/* AI 分析功能 */}
      <div className="grid md:grid-cols-2 gap-6">
        {[
          {
            title: '市场趋势分析',
            description: 'AI 分析当前教育市场热点，发现课程机会',
            icon: TrendingUp,
            color: 'from-blue-500 to-cyan-600',
            actions: ['查看热门方向', '竞争对手分析', '定价建议']
          },
          {
            title: '学生画像分析',
            description: '深入了解学生学习习惯，优化教学策略',
            icon: Users,
            color: 'from-purple-500 to-pink-600',
            actions: ['学生分布', '学习进度', '满意度分析']
          },
          {
            title: '课程优化建议',
            description: 'AI 根据反馈数据，提供课程改进建议',
            icon: Lightbulb,
            color: 'from-amber-500 to-orange-600',
            actions: ['内容建议', '时长优化', '互动设计']
          },
          {
            title: '收入预测',
            description: '基于历史数据预测未来收入趋势',
            icon: BarChart3,
            color: 'from-emerald-500 to-teal-600',
            actions: ['月度预测', '增长机会', '风险提示']
          }
        ].map((feature) => (
          <div key={feature.title} className="bg-white rounded-2xl border border-slate-100 p-6 hover:shadow-lg transition-shadow">
            <div className="flex items-center gap-4 mb-4">
              <div className={`w-12 h-12 rounded-xl bg-gradient-to-br ${feature.color} flex items-center justify-center text-white shadow-lg`}>
                <feature.icon className="w-6 h-6" />
              </div>
              <div>
                <h3 className="font-bold text-slate-900">{feature.title}</h3>
                <p className="text-sm text-slate-500">{feature.description}</p>
              </div>
            </div>
            <div className="flex flex-wrap gap-2">
              {feature.actions.map((action) => (
                <button
                  key={action}
                  onClick={onOpenChat}
                  className="px-4 py-2 bg-slate-50 hover:bg-slate-100 text-slate-700 text-sm font-medium rounded-lg transition-colors"
                >
                  {action}
                </button>
              ))}
            </div>
          </div>
        ))}
      </div>

      {/* AI 快速操作 */}
      <div className="bg-gradient-to-br from-emerald-500 to-teal-600 rounded-3xl p-8 text-white">
        <div className="flex items-center justify-between">
          <div>
            <h3 className="text-2xl font-bold mb-2">AI 教学助手</h3>
            <p className="text-emerald-100 max-w-md">让 AI 帮您分析教学数据、优化课程内容、管理学生关系，提升教学效率和收入</p>
          </div>
          <button
            onClick={onOpenChat}
            className="flex items-center gap-2 px-6 py-3 bg-white text-emerald-700 rounded-xl font-bold shadow-lg hover:shadow-xl transition-all"
          >
            <MessageCircle className="w-5 h-5" />
            开始对话
          </button>
        </div>
      </div>
    </div>
  );
};

// 管理员端 AI 功能
const AdminAIFeatures: React.FC<{ onOpenChat: () => void }> = ({ onOpenChat }) => {
  return (
    <div className="space-y-8">
      {/* 平台核心指标 */}
      <div className="grid md:grid-cols-5 gap-4">
        {[
          { label: '总用户数', value: '2,845', change: '+12.5%', icon: Users, color: 'blue' },
          { label: '活跃教师', value: '186', change: '+8.3%', icon: GraduationCap, color: 'emerald' },
          { label: '月收入', value: '¥156K', change: '+18.2%', icon: DollarSign, color: 'purple' },
          { label: '课程完成率', value: '94.2%', change: '+2.1%', icon: CheckCircle, color: 'amber' },
          { label: '用户满意度', value: '4.8', change: '+0.3', icon: Heart, color: 'pink' }
        ].map((stat) => (
          <div key={stat.label} className="bg-white rounded-2xl border border-slate-100 p-4 hover:shadow-md transition-shadow">
            <div className="flex items-center justify-between mb-2">
              <div className={`w-9 h-9 rounded-lg bg-${stat.color}-100 flex items-center justify-center`}>
                <stat.icon className={`w-4 h-4 text-${stat.color}-600`} />
              </div>
              <span className="text-[10px] text-emerald-600 font-medium bg-emerald-50 px-1.5 py-0.5 rounded-full">{stat.change}</span>
            </div>
            <div className="text-xl font-bold text-slate-900">{stat.value}</div>
            <div className="text-[10px] text-slate-500">{stat.label}</div>
          </div>
        ))}
      </div>

      {/* AI 管理功能 */}
      <div className="grid md:grid-cols-3 gap-6">
        {[
          {
            title: '平台数据分析',
            description: '全面分析平台运营数据，洞察业务健康度',
            icon: BarChart3,
            color: 'from-slate-700 to-slate-900',
            badge: '实时'
          },
          {
            title: '用户行为分析',
            description: '深度分析用户行为，提升用户体验和留存',
            icon: Activity,
            color: 'from-blue-500 to-indigo-600',
            badge: '热门'
          },
          {
            title: '风险预警系统',
            description: '智能识别潜在风险，提前预警',
            icon: Shield,
            color: 'from-rose-500 to-pink-600',
            badge: '重要'
          },
          {
            title: 'AI 运营建议',
            description: '基于数据分析提供精准的运营策略建议',
            icon: Lightbulb,
            color: 'from-amber-500 to-orange-600',
            badge: '新'
          },
          {
            title: '收入趋势分析',
            description: '收入构成分析，发现增长机会',
            icon: TrendingUp,
            color: 'from-emerald-500 to-teal-600',
            badge: ''
          },
          {
            title: '系统性能优化',
            description: '系统性能分析和优化建议',
            icon: Zap,
            color: 'from-purple-500 to-violet-600',
            badge: ''
          }
        ].map((feature) => (
          <button
            key={feature.title}
            onClick={onOpenChat}
            className="group bg-white rounded-2xl border border-slate-100 p-6 text-left hover:shadow-xl hover:border-slate-200 transition-all"
          >
            <div className="flex items-start justify-between mb-4">
              <div className={`w-12 h-12 rounded-xl bg-gradient-to-br ${feature.color} flex items-center justify-center text-white shadow-lg group-hover:scale-110 transition-transform`}>
                <feature.icon className="w-6 h-6" />
              </div>
              {feature.badge && (
                <span className="text-[10px] font-bold text-white bg-gradient-to-r from-indigo-500 to-purple-500 px-2 py-0.5 rounded-full">
                  {feature.badge}
                </span>
              )}
            </div>
            <h3 className="font-bold text-slate-900 mb-1 group-hover:text-indigo-600 transition-colors">{feature.title}</h3>
            <p className="text-sm text-slate-500">{feature.description}</p>
            <div className="mt-4 flex items-center text-sm text-indigo-600 font-medium opacity-0 group-hover:opacity-100 transition-opacity">
              <span>开始分析</span>
              <ArrowRight className="w-4 h-4 ml-1 group-hover:translate-x-1 transition-transform" />
            </div>
          </button>
        ))}
      </div>

      {/* AI 控制台 */}
      <div className="bg-gradient-to-br from-slate-800 via-slate-900 to-slate-950 rounded-3xl p-8 text-white">
        <div className="flex items-center justify-between">
          <div>
            <div className="flex items-center gap-2 mb-2">
              <Brain className="w-6 h-6 text-indigo-400" />
              <h3 className="text-2xl font-bold">AI 运营控制台</h3>
            </div>
            <p className="text-slate-400 max-w-lg">数据驱动的智能运营平台，实时监控、风险预警、策略建议，让平台管理更高效</p>
          </div>
          <button
            onClick={onOpenChat}
            className="flex items-center gap-2 px-6 py-3 bg-gradient-to-r from-indigo-500 to-purple-600 text-white rounded-xl font-bold shadow-lg hover:shadow-xl transition-all"
          >
            <MessageCircle className="w-5 h-5" />
            AI 对话
          </button>
        </div>
      </div>
    </div>
  );
};

type ActiveTab = 'features' | 'recommendations' | 'preferences' | 'analytics';

export const AIPage: React.FC = () => {
  const { user } = useAuthStore();
  const userRole = user?.role || 'student';
  const [activeTab, setActiveTab] = useState<ActiveTab>('features');
  const [chatbotOpen, setChatbotOpen] = useState(false);

  const getRoleConfig = () => {
    switch (userRole) {
      case 'teacher':
        return {
          title: '教师 AI 助手',
          subtitle: '提升教学效率，优化收入增长',
          gradient: 'from-emerald-500 via-teal-500 to-cyan-600',
          icon: GraduationCap
        };
      case 'admin':
        return {
          title: '平台 AI 控制台',
          subtitle: '数据驱动，智能运营',
          gradient: 'from-slate-700 via-slate-800 to-slate-900',
          icon: Shield
        };
      default:
        return {
          title: '学习 AI 伙伴',
          subtitle: '个性化学习，陪伴成长',
          gradient: 'from-blue-500 via-indigo-500 to-purple-600',
          icon: Brain
        };
    }
  };

  const config = getRoleConfig();

  const tabs = [
    { id: 'features' as const, label: 'AI 功能', icon: Zap },
    { id: 'recommendations' as const, label: '智能推荐', icon: Sparkles },
    { id: 'preferences' as const, label: '偏好设置', icon: Settings },
    { id: 'analytics' as const, label: '数据分析', icon: BarChart3 }
  ];

  const renderContent = () => {
    switch (activeTab) {
      case 'features':
        if (userRole === 'teacher') return <TeacherAIFeatures onOpenChat={() => setChatbotOpen(true)} />;
        if (userRole === 'admin') return <AdminAIFeatures onOpenChat={() => setChatbotOpen(true)} />;
        return <StudentAIFeatures onOpenChat={() => setChatbotOpen(true)} />;
      case 'recommendations':
        return <SmartRecommendations showType="both" limit={12} />;
      case 'preferences':
        return <PreferencesSettings onPreferencesSaved={() => setActiveTab('features')} />;
      case 'analytics':
        return <LearningAnalytics />;
      default:
        return null;
    }
  };

  return (
    <div className="min-h-screen bg-slate-50">
      {/* 页面头部 */}
      <div className={`relative overflow-hidden bg-gradient-to-br ${config.gradient}`}>
        <div className="absolute inset-0 opacity-30">
          <div className="absolute -top-16 left-10 w-72 h-72 bg-white/20 blur-[100px]" />
          <div className="absolute top-0 right-0 w-[420px] h-[420px] bg-white/10 blur-[120px]" />
        </div>
        <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12 sm:py-16">
          <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-6">
            <div className="text-white">
              <div className="flex items-center gap-3 mb-3">
                <div className="w-12 h-12 bg-white/20 backdrop-blur-sm rounded-2xl flex items-center justify-center">
                  <config.icon className="w-6 h-6" />
                </div>
                <div>
                  <h1 className="text-2xl sm:text-3xl font-bold">{config.title}</h1>
                  <p className="text-white/80 text-sm">{config.subtitle}</p>
                </div>
              </div>
            </div>
            <button
              onClick={() => setChatbotOpen(true)}
              className="flex items-center justify-center gap-2 px-6 py-3 bg-white text-slate-900 rounded-xl font-bold shadow-xl hover:shadow-2xl hover:scale-105 transition-all"
            >
              <MessageCircle className="w-5 h-5" />
              开始 AI 对话
            </button>
          </div>
        </div>
      </div>

      {/* 标签导航 */}
      <div className="bg-white border-b border-slate-200 sticky top-0 z-30">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex overflow-x-auto scrollbar-hide -mx-4 px-4 sm:mx-0 sm:px-0">
            {tabs.map((tab) => (
              <button
                key={tab.id}
                onClick={() => setActiveTab(tab.id)}
                className={`flex items-center gap-2 px-4 sm:px-6 py-4 border-b-2 font-medium text-sm whitespace-nowrap transition-all ${
                  activeTab === tab.id
                    ? 'border-indigo-500 text-indigo-600'
                    : 'border-transparent text-slate-500 hover:text-slate-700 hover:border-slate-300'
                }`}
              >
                <tab.icon className="w-4 h-4 sm:w-5 sm:h-5" />
                <span>{tab.label}</span>
              </button>
            ))}
          </div>
        </div>
      </div>

      {/* 主要内容区域 */}
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {renderContent()}
      </div>

      {/* AI 聊天机器人 */}
      <RoleBasedAIChatbot 
        isOpen={chatbotOpen} 
        onClose={() => setChatbotOpen(false)} 
      />
      
      {!chatbotOpen && (
        <RoleBasedChatbotTrigger onClick={() => setChatbotOpen(true)} />
      )}
    </div>
  );
};

// 学习分析组件
const LearningAnalytics: React.FC = () => {
  return (
    <div className="space-y-8">
      {/* 统计卡片 */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 sm:gap-6">
        {[
          { title: '完成课程', value: '24', change: '+12%', icon: BookOpen, color: 'blue' },
          { title: '学习时长', value: '156h', change: '+8%', icon: Clock, color: 'emerald' },
          { title: '平均评分', value: '4.8', change: '+0.2', icon: Star, color: 'amber' },
          { title: '推荐采纳率', value: '85%', change: '+5%', icon: TrendingUp, color: 'purple' }
        ].map((stat) => (
          <div key={stat.title} className="bg-white rounded-2xl border border-slate-100 p-5 hover:shadow-md transition-shadow">
            <div className="flex items-center justify-between mb-3">
              <div className={`w-10 h-10 rounded-xl bg-${stat.color}-100 flex items-center justify-center`}>
                <stat.icon className={`w-5 h-5 text-${stat.color}-600`} />
              </div>
              <span className="text-xs text-emerald-600 font-medium">{stat.change}</span>
            </div>
            <div className="text-2xl font-bold text-slate-900">{stat.value}</div>
            <div className="text-xs text-slate-500">{stat.title}</div>
          </div>
        ))}
      </div>

      {/* 学习进度图表 */}
      <div className="bg-white rounded-2xl border border-slate-100 p-6">
        <h3 className="text-lg font-bold text-slate-900 mb-4 flex items-center gap-2">
          <LineChart className="w-5 h-5 text-indigo-500" />
          学习进度趋势
        </h3>
        <div className="h-64 bg-gradient-to-br from-slate-50 to-indigo-50/30 rounded-xl flex items-center justify-center border border-slate-100">
          <div className="text-center">
            <BarChart3 className="w-12 h-12 text-slate-300 mx-auto mb-2" />
            <p className="text-slate-500">图表区域</p>
          </div>
        </div>
      </div>

      {/* AI 洞察 */}
      <div className="bg-white rounded-2xl border border-slate-100 p-6">
        <div className="flex items-center gap-3 mb-6">
          <div className="w-10 h-10 bg-gradient-to-br from-purple-500 to-pink-500 rounded-xl flex items-center justify-center text-white">
            <Sparkles className="w-5 h-5" />
          </div>
          <div>
            <h3 className="text-lg font-bold text-slate-900">AI 洞察与建议</h3>
            <p className="text-sm text-slate-500">基于您的学习数据生成</p>
          </div>
        </div>
        
        <div className="space-y-4">
          {[
            { type: 'success', title: '学习表现优异', content: '您在数学课程方面表现出色，建议继续保持并考虑进阶课程。' },
            { type: 'info', title: '学习时间建议', content: '根据您的学习习惯，建议在下午2-4点安排重要课程，此时学习效率最高。' },
            { type: 'warning', title: '学习平衡提醒', content: '建议在英语学习上投入更多时间，以平衡各科目的发展。' }
          ].map((insight, index) => (
            <div key={index} className={`p-4 rounded-xl border-l-4 ${
              insight.type === 'success' ? 'bg-emerald-50 border-emerald-400' :
              insight.type === 'info' ? 'bg-blue-50 border-blue-400' :
              'bg-amber-50 border-amber-400'
            }`}>
              <h4 className={`font-semibold ${
                insight.type === 'success' ? 'text-emerald-800' :
                insight.type === 'info' ? 'text-blue-800' :
                'text-amber-800'
              }`}>
                {insight.title}
              </h4>
              <p className={`text-sm mt-1 ${
                insight.type === 'success' ? 'text-emerald-700' :
                insight.type === 'info' ? 'text-blue-700' :
                'text-amber-700'
              }`}>
                {insight.content}
              </p>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
};
